import { ActionIcon, Checkbox, Tooltip } from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { getConnectorRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';


export const ConnectorPlacementSettings = () => {
    const { t } = useTranslation();
    const [includeNeighboringPois, setIncludeNeighboringPois] = useState<boolean>(false);

    const { articles, pois, selectedPoiIndex, removeArticleInstances } = usePlacementStore(
        useShallow((state) => ({
            articles: state.articles,
            pois: state.pois,
            selectedPoiIndex: state.poiSelection.current.index,
            removeArticleInstances: state.removeArticleInstances
        }))
    );

    const selectedPoi = useMemo(
        () => (selectedPoiIndex === undefined ? undefined : pois[selectedPoiIndex]),
        [selectedPoiIndex, pois]
    );

    const affectedPoiIndices = useMemo(() => {
        if (selectedPoiIndex === undefined || !selectedPoi) {
            return [];
        }

        return includeNeighboringPois ? getConnectorRelatedPoiIndices(pois, selectedPoiIndex) : [selectedPoiIndex];
    }, [includeNeighboringPois, pois, selectedPoi, selectedPoiIndex]);

    const affectedPoiGuids = useMemo(
        () => new Set(affectedPoiIndices.map((index) => pois[index]?.poi.poiGuid).filter(Boolean)),
        [affectedPoiIndices, pois]
    );

    const connectorInstancesByArticle = useMemo(() => {
        const result: Record<string, string[]> = {};
        if (affectedPoiGuids.size === 0) {
            return result;
        }

        Object.entries(articles).forEach(([articleId, article]) => {
            if (!isFramiClamp(article.articleNumber)) {
                return;
            }

            const instanceIds = Object.entries(article.articleInstances)
                .filter(([instanceId, instance]) => {
                    if (instance.poiGuids?.length) {
                        return instance.poiGuids.some((poiGuid) => affectedPoiGuids.has(poiGuid));
                    }

                    return affectedPoiIndices.some((poiIndex) =>
                        pois[poiIndex]?.articles[articleId]?.includes(instanceId)
                    );
                })
                .map(([instanceId]) => instanceId);

            if (instanceIds.length > 0) {
                result[articleId] = instanceIds;
            }
        });

        return result;
    }, [affectedPoiGuids, affectedPoiIndices, articles, pois]);

    const connectorPlacementCount = Object.values(connectorInstancesByArticle).reduce(
        (count, instanceIds) => count + instanceIds.length,
        0
    );

    const handleOnRemovePlacements = useCallback(() => {
        if (connectorPlacementCount === 0) {
            return;
        }

        removeArticleInstances(connectorInstancesByArticle, true);
    }, [connectorPlacementCount, connectorInstancesByArticle, removeArticleInstances]);

    return (
        <section
            aria-labelledby="connector-cleanup-heading"
            className="flex flex-col gap-2 rounded-sm border border-border-ultra-light bg-background-gray-ultra-light p-2"
        >
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide" id="connector-cleanup-heading">
                    {t('labelConnectorCleanup')}
                </h3>
            </div>
            <p className="text-xs leading-snug text-text-gray-dark">{t('infoConnectorCleanup')}</p>
            <Checkbox
                checked={includeNeighboringPois}
                label={t('labelIncludeNeighboringPois')}
                styles={{
                    label: {
                        fontSize: '14px'
                    }
                }}
                onChange={(event) => setIncludeNeighboringPois(event.currentTarget.checked)}
            />
            <div className="mt-2 flex w-full flex-row items-center">
                <Tooltip
                    label={
                        connectorPlacementCount === 0
                            ? t('labelDeleteAllConnectorPlacementsDisabled')
                            : t('labelDeleteAllConnectorPlacements')
                    }
                >
                    <ActionIcon
                        aria-label={t('labelDeleteAllConnectorPlacements')}
                        className="bg-background-gray disabled:opacity-50 text-text-accent pointer-events-auto action-icon-medium automated-placement-action-icon max-[1440px]:h-7.5 max-[1440px]:w-7.5 max-[1440px]:text-icon-sm max-[850px]:h-7.5 max-[850px]:w-7.5 max-[850px]:text-icon-sm"
                        color="red"
                        disabled={connectorPlacementCount === 0}
                        onClick={handleOnRemovePlacements}
                    >
                        delete
                    </ActionIcon>
                </Tooltip>

                <span className="px-2 text-sm">
                    {t('labelDeleteAllConnectorPlacements')} ({connectorPlacementCount})
                </span>
            </div>
        </section>
    );
};
